/**
 * Single-dialog multi-bucket credit adjustment flow (DNKADM-0157 U2).
 *
 * The Credits tab itself is read-only; the header "Adjust credits" button opens
 * this dialog. Adds are one grant-only `adjustMemberCredits` batch; removes call
 * `reduceBucketCredits` once per bucket with positive amounts. The shared reason
 * and grant expiry behavior remain in one staff-facing flow.
 *
 * DNKADM-0479 (F-244): each of those writes carries its own `idempotencyKey`, minted when
 * Save is pressed. A write that gets no answer (a network error, timeout, 5xx or a retryable
 * `409`) keeps its exact body under its key, as does every write not yet sent, and the dialog
 * locks; only "Check again" re-sends them, and dink-api grants or removes nothing a second
 * time. Writes the server answered are never re-sent.
 */
import { useId, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  adjustMemberCredits,
  reduceBucketCredits,
  type AdjustCreditsBatchInput,
  type CreditAdjustBatchResult,
  type CreditBalance,
} from '@/api/members';
import { newIdempotencyKey } from '@/api/payments';
import { IconAction } from '@/components/IconAction';
import { ApiError } from '@/api/errors';
import { v2ErrorCode } from '@/api/v2Errors';
import { useToast } from '@/components/useToast';
import { Icon, inkAlpha, MONO, resolveMaps, resolveTokens } from '@/theme';
import { formatCredits } from '@/utils/v2Format';
import { boundaryInstantForLastUsableDay } from '@/utils/creditExpiry';
import { useCalendarZone } from '@/location/useCalendarZone';
import { toCreditAdjustErrorMessage } from './membersError';
import {
  authRefusalMessage,
  outcomeIsUnknown,
  unconfirmedMessage,
  UNCONFIRMED_CREDITS_CLOSE_PROMPT,
} from './unconfirmedOutcome';

type Direction = 'grant' | 'debit';

interface PendingEdit {
  direction: Direction;
  magnitude: string;
  expiresAt: string;
}

interface ReductionFailure {
  bucketId: string;
  label: string;
  message: string;
  insufficientCredits: boolean;
}

type AdjustableBucket = CreditBalance & { creditBucketId: string };

/**
 * DNKADM-0479: one write of a save, exactly as it is sent, under its own key. The grant is
 * one batch; each reduction is its own call.
 */
type CreditWrite =
  | { kind: 'grant'; bucketIds: string[]; input: AdjustCreditsBatchInput }
  | {
      kind: 'reduce';
      bucketId: string;
      label: string;
      amount: number;
      reason: string;
      idempotencyKey: string;
    };

/**
 * DNKADM-0479: a save the server never confirmed either way. `writes` are the ones that got
 * no answer, plus any not yet sent; the rest is what the save already settled, carried so the
 * result after "Check again" reads as one save.
 */
interface UnconfirmedSave {
  writes: CreditWrite[];
  failures: ReductionFailure[];
  applied: number;
  total: number;
  grantResult: CreditAdjustBatchResult | null;
}

const MIN_REASON = 4;
const AMOUNT_WIDTH = 84;
const EMPTY_ADJUST_RESULT: CreditAdjustBatchResult = { adjustmentBatchId: null, results: [] };

function bucketLabel(b: CreditBalance): string {
  return b.bucketName ?? 'Bucket';
}

/**
 * A "Good through" day already past would expire the grant on arrival; refuse it
 * (DNKADM-0456 V-77). Today stays valid: only the day's exclusive boundary is tested.
 */
function isPastExpiryDay(value: string, zone: string | null): boolean {
  const day = value.trim();
  if (day.length === 0) return false;
  const boundary = Date.parse(boundaryInstantForLastUsableDay(day, zone));
  return Number.isFinite(boundary) && boundary <= Date.now();
}

function blankEdit(direction: Direction = 'grant'): PendingEdit {
  return { direction, magnitude: '', expiresAt: '' };
}

function creditChangeCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'change' : 'changes'}`;
}

function formatReduceFailures(failures: ReductionFailure[]): string {
  return failures
    .map((failure) => `couldn't reduce ${failure.label}: ${failure.message}`)
    .join('; ');
}

function toPartialFailureMessage(
  appliedChanges: number,
  totalChanges: number,
  failures: ReductionFailure[],
): string | null {
  if (failures.length === 0) return null;
  if (appliedChanges === 0 && totalChanges === 1) {
    return failures[0]!.insufficientCredits ? null : failures[0]!.message;
  }
  const failedText = formatReduceFailures(failures);
  if (appliedChanges > 0) {
    return `Applied ${creditChangeCountLabel(appliedChanges)}; ${failedText}`;
  }
  return failedText.charAt(0).toUpperCase() + failedText.slice(1);
}

interface AdjustCreditsDialogProps {
  contactId: string;
  open: boolean;
  buckets: CreditBalance[];
  onClose: () => void;
  onAdjusted: (result: CreditAdjustBatchResult) => void;
}

export function AdjustCreditsDialog({
  contactId,
  open,
  buckets,
  onClose,
  onAdjusted,
}: AdjustCreditsDialogProps) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const toast = useToast();
  const ids = useId();
  const titleId = `${ids}-title`;
  const reasonId = `${ids}-reason`;
  // Lot days are on the location calendar (DNKADM-0439, Operator ruling 2026-10-15).
  const zone = useCalendarZone();

  const [edits, setEdits] = useState<Record<string, PendingEdit>>({});
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [bucketErrors, setBucketErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  /** DNKADM-0479: a save with writes that got no answer, kept as sent for "Check again". */
  const [unconfirmed, setUnconfirmed] = useState<UnconfirmedSave | null>(null);
  const locked = submitting || unconfirmed !== null;

  const adjustableBuckets = buckets.filter(
    (b): b is AdjustableBucket => b.creditBucketId !== null,
  );

  function getEdit(bucketId: string): PendingEdit {
    return edits[bucketId] ?? blankEdit();
  }

  function getDelta(bucketId: string): number | null {
    const edit = edits[bucketId];
    if (!edit) return null;
    const mag = Number(edit.magnitude);
    if (!Number.isFinite(mag) || mag <= 0) return null;
    return edit.direction === 'grant' ? mag : -mag;
  }

  const pendingBuckets = adjustableBuckets.filter((b) => getDelta(b.creditBucketId) !== null);
  const hasPending = pendingBuckets.length > 0;
  const hasOverDebit = adjustableBuckets.some((b) => {
    const delta = getDelta(b.creditBucketId);
    return delta !== null && delta < 0 && Math.abs(delta) > b.balance;
  });
  const reasonOk = reason.trim().length >= MIN_REASON;
  function isExpiryPast(bucketId: string): boolean {
    const edit = edits[bucketId];
    return edit !== undefined && edit.direction === 'grant' && isPastExpiryDay(edit.expiresAt, zone);
  }
  const hasPastExpiry = adjustableBuckets.some((b) => isExpiryPast(b.creditBucketId));
  const canSubmit = hasPending && reasonOk && !hasOverDebit && !hasPastExpiry && !locked;

  function resetDraft() {
    setEdits({});
    setReason('');
    setError(null);
    setBucketErrors({});
    setSubmitting(false);
    setUnconfirmed(null);
  }

  function updateEdit(bucketId: string, updater: (prev: PendingEdit) => PendingEdit) {
    setEdits((prev) => ({ ...prev, [bucketId]: updater(prev[bucketId] ?? blankEdit()) }));
    setBucketErrors((prev) => {
      if (!(bucketId in prev)) return prev;
      const next = { ...prev };
      delete next[bucketId];
      return next;
    });
  }

  function clearEdit(bucketId: string) {
    setEdits((prev) => {
      const next = { ...prev };
      delete next[bucketId];
      return next;
    });
    setBucketErrors((prev) => {
      if (!(bucketId in prev)) return prev;
      const next = { ...prev };
      delete next[bucketId];
      return next;
    });
  }

  function clearDraftBucketIds(bucketIds: readonly string[]) {
    if (bucketIds.length === 0) return;
    const bucketSet = new Set(bucketIds);
    setEdits((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const bucketId of bucketSet) {
        if (bucketId in next) {
          delete next[bucketId];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setBucketErrors((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const bucketId of bucketSet) {
        if (bucketId in next) {
          delete next[bucketId];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }

  function close() {
    if (submitting) return;
    // DNKADM-0479: a credit change that may still land is never left silently.
    if (unconfirmed !== null && !window.confirm(UNCONFIRMED_CREDITS_CLOSE_PROMPT)) return;
    resetDraft();
    onClose();
  }

  async function handleSave() {
    if (!canSubmit) return;
    const grantAdjustments = pendingBuckets
      .filter((b) => getEdit(b.creditBucketId).direction === 'grant')
      .map((b) => {
        const edit = getEdit(b.creditBucketId);
        const amount = getDelta(b.creditBucketId)!;
        const adjustment: { creditBucketId: string; amount: number; expiresAt?: string } = {
          creditBucketId: b.creditBucketId,
          amount,
        };
        if (edit.expiresAt.trim().length > 0) {
          // The picked day is the LAST USABLE day; the wire carries the exclusive
          // boundary (the start of picked + 1 in the calendar zone, DNKADM-0306/0439).
          adjustment.expiresAt = boundaryInstantForLastUsableDay(edit.expiresAt.trim(), zone);
        }
        return adjustment;
      });
    const grantBucketIds = pendingBuckets
      .filter((b) => getEdit(b.creditBucketId).direction === 'grant')
      .map((b) => b.creditBucketId);
    const writes: CreditWrite[] = [];
    if (grantAdjustments.length > 0) {
      writes.push({
        kind: 'grant',
        bucketIds: grantBucketIds,
        input: {
          reason: reason.trim(),
          adjustments: grantAdjustments,
          idempotencyKey: newIdempotencyKey(),
        },
      });
    }
    for (const b of pendingBuckets) {
      if (getEdit(b.creditBucketId).direction !== 'debit') continue;
      writes.push({
        kind: 'reduce',
        bucketId: b.creditBucketId,
        label: bucketLabel(b),
        amount: Math.abs(getDelta(b.creditBucketId)!),
        reason: reason.trim(),
        idempotencyKey: newIdempotencyKey(),
      });
    }
    await runWrites({
      writes,
      failures: [],
      applied: 0,
      total: writes.reduce(
        (sum, w) => sum + (w.kind === 'grant' ? w.input.adjustments.length : 1),
        0,
      ),
      grantResult: null,
    });
  }

  /** DNKADM-0479: settle an unconfirmed save by re-sending its writes under their own keys. */
  async function checkAgain() {
    if (unconfirmed === null || submitting) return;
    await runWrites(unconfirmed);
  }

  /** The one write path, in order: the grant, then each reduction. Press and "Check again". */
  async function runWrites(save: UnconfirmedSave) {
    const { writes, total: totalChanges } = save;
    // DNKADM-0480: a "Check again" run re-sends writes the server may already have applied.
    const resend = unconfirmed !== null;
    setError(null);
    setBucketErrors({});
    setSubmitting(true);

    let grantResult = save.grantResult;
    let appliedChanges = save.applied;
    const grant = writes.find((w) => w.kind === 'grant');
    if (grant !== undefined) {
      try {
        grantResult = await adjustMemberCredits(contactId, grant.input);
        appliedChanges += grant.input.adjustments.length;
        clearDraftBucketIds(grant.bucketIds);
      } catch (err) {
        onAdjusted(EMPTY_ADJUST_RESULT);
        setSubmitting(false);
        if (outcomeIsUnknown(err, resend)) {
          // The grant may have landed and nothing after it was sent: keep the whole save.
          setUnconfirmed(save);
          // DNKADM-0480: a re-send refused by auth keeps the lock and says why.
          setError(authRefusalMessage(err));
          return;
        }
        setUnconfirmed(null);
        setError(toCreditAdjustErrorMessage(err));
        return;
      }
    }

    const failures: ReductionFailure[] = [...save.failures];
    const unanswered: CreditWrite[] = [];
    let unansweredAuthLine: string | null = null;
    for (const reduction of writes) {
      if (reduction.kind !== 'reduce') continue;
      try {
        await reduceBucketCredits(contactId, reduction.bucketId, {
          amount: reduction.amount,
          reason: reduction.reason,
          idempotencyKey: reduction.idempotencyKey,
        });
        appliedChanges += 1;
        clearDraftBucketIds([reduction.bucketId]);
      } catch (err) {
        if (outcomeIsUnknown(err, resend)) {
          unanswered.push(reduction);
          unansweredAuthLine ??= authRefusalMessage(err);
          continue;
        }
        const insufficientCredits =
          err instanceof ApiError &&
          err.status === 409 &&
          v2ErrorCode(err) === 'INSUFFICIENT_CREDITS';
        failures.push({
          bucketId: reduction.bucketId,
          label: reduction.label,
          message: insufficientCredits
            ? `Not enough credits in ${reduction.label}. Reloaded the latest balance.`
            : toCreditAdjustErrorMessage(err),
          insufficientCredits,
        });
      }
    }

    if (unanswered.length > 0) {
      // A reduction may have landed: lock on the unanswered ones. A refused one is still shown;
      // its bucket stays in the draft for after the lock settles.
      const nextBucketErrors: Record<string, string> = {};
      for (const failure of failures) {
        if (failure.insufficientCredits) nextBucketErrors[failure.bucketId] = failure.message;
      }
      setBucketErrors(nextBucketErrors);
      const partial = toPartialFailureMessage(appliedChanges, totalChanges, failures);
      // DNKADM-0480: a re-send refused by auth keeps the lock and says why.
      setError(
        unansweredAuthLine === null
          ? partial
          : partial === null
            ? unansweredAuthLine
            : `${partial} ${unansweredAuthLine}`,
      );
      setUnconfirmed({
        writes: unanswered,
        failures,
        applied: appliedChanges,
        total: totalChanges,
        grantResult,
      });
      onAdjusted(grantResult ?? EMPTY_ADJUST_RESULT);
      setSubmitting(false);
      return;
    }
    setUnconfirmed(null);

    if (failures.length > 0) {
      const nextBucketErrors: Record<string, string> = {};
      for (const failure of failures) {
        if (failure.insufficientCredits) {
          nextBucketErrors[failure.bucketId] = failure.message;
        }
      }
      setBucketErrors(nextBucketErrors);
      setError(toPartialFailureMessage(appliedChanges, totalChanges, failures));
      onAdjusted(grantResult ?? EMPTY_ADJUST_RESULT);
      setSubmitting(false);
      return;
    }

    const result = grantResult ?? EMPTY_ADJUST_RESULT;
    onAdjusted(result);
    resetDraft();
    onClose();
    const batchId = result.adjustmentBatchId;
    toast(batchId ? `Credits updated. Batch ID: ${batchId}` : 'Credits updated successfully.');
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}>
        <Avatar variant="rounded" sx={{ bgcolor: t.brandSoft, color: t.brand, width: 38, height: 38 }}>
          <Icon name="account_balance_wallet" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Adjust credits</Typography>
          <Typography variant="body2">Edit one or more buckets, then save the changes.</Typography>
        </Box>
        <IconAction size="small" onClick={close} label="Close" disabled={submitting}>
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 1.5,
            mb: 2.5,
          }}
        >
          {adjustableBuckets.map((bucket) => {
            const edit = getEdit(bucket.creditBucketId);
            const delta = getDelta(bucket.creditBucketId);
            const overDebit =
              delta !== null && delta < 0 && Math.abs(delta) > bucket.balance;
            return (
              <BucketAdjustCard
                key={bucket.creditBucketId}
                bucket={bucket}
                edit={edit}
                delta={delta}
                overDebit={overDebit}
                expiryPast={isExpiryPast(bucket.creditBucketId)}
                serverError={bucketErrors[bucket.creditBucketId] ?? null}
                disabled={locked}
                onClear={() => clearEdit(bucket.creditBucketId)}
                onDirectionChange={(direction) =>
                  updateEdit(bucket.creditBucketId, (prev) => ({ ...prev, direction }))
                }
                onMagnitudeChange={(magnitude) =>
                  updateEdit(bucket.creditBucketId, (prev) => ({ ...prev, magnitude }))
                }
                onExpiresAtChange={(expiresAt) =>
                  updateEdit(bucket.creditBucketId, (prev) => ({ ...prev, expiresAt }))
                }
              />
            );
          })}
        </Box>

        <TextField
          id={reasonId}
          label="Reason for adjustment"
          required
          multiline
          minRows={3}
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={locked}
          placeholder="e.g. Goodwill credit for court maintenance closure"
          {...(reasonOk ? { color: 'success' as const } : {})}
          {...(reason.length > 0 ? { focused: true } : {})}
          helperText={
            reason.length === 0
              ? 'Required — logged to the audit trail so the team has context.'
              : reasonOk
                ? 'Looks good.'
                : 'A little more detail helps (at least a few words).'
          }
        />

        {unconfirmed !== null && (
          <Alert
            severity="warning"
            role="alert"
            sx={{ mt: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void checkAgain()}
                disabled={submitting}
              >
                Check again
              </Button>
            }
          >
            {unconfirmedMessage('credits')}
          </Alert>
        )}
        {error && (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={close} disabled={submitting} sx={{ color: t.text2 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<Icon name="check" />}
          onClick={() => void handleSave()}
          disabled={!canSubmit}
        >
          {submitting ? 'Saving…' : 'Save credit changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BucketAdjustCard({
  bucket,
  edit,
  delta,
  overDebit,
  expiryPast,
  serverError,
  disabled,
  onClear,
  onDirectionChange,
  onMagnitudeChange,
  onExpiresAtChange,
}: {
  bucket: AdjustableBucket;
  edit: PendingEdit;
  delta: number | null;
  overDebit: boolean;
  expiryPast: boolean;
  serverError: string | null;
  disabled: boolean;
  onClear: () => void;
  onDirectionChange: (direction: Direction) => void;
  onMagnitudeChange: (magnitude: string) => void;
  onExpiresAtChange: (expiresAt: string) => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const maps = resolveMaps(theme);
  const ids = useId();
  const magnitudeId = `${ids}-magnitude`;
  const label = bucketLabel(bucket);
  const newBalance = bucket.balance + (delta ?? 0);

  return (
    <Box
      sx={{
        p: '14px 16px',
        borderRadius: 2,
        border: '1px solid',
        borderColor:
          overDebit || serverError !== null
            ? t.error
            : delta !== null
              ? t.brand
              : inkAlpha(theme, 0.14),
        bgcolor: delta !== null && !overDebit ? maps.greenTints.surfaceA : t.surface,
        minWidth: 0,
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{label}</Typography>
          <Typography sx={{ fontSize: 11, color: inkAlpha(theme, 0.5) }}>
            Current balance:&nbsp;
            <span style={{ fontFamily: MONO, fontWeight: 600 }}>{formatCredits(bucket.balance)}</span>
          </Typography>
        </Box>
        {delta !== null && (
          <IconAction
            size="small"
            label={`Clear ${label} adjustment`}
            onClick={onClear}
            disabled={disabled}
            sx={{ color: inkAlpha(theme, 0.4) }}
          >
            <Icon name="close" sx={{ fontSize: 18 }} />
          </IconAction>
        )}
      </Stack>

      <Stack direction="row" sx={{ gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={edit.direction}
          onChange={(_e, v: Direction | null) => v && onDirectionChange(v)}
          aria-label={`${label} direction`}
          disabled={disabled}
        >
          <ToggleButton value="grant" aria-label={`Add ${label}`}>
            <Icon name="add" sx={{ fontSize: 16, mr: 0.5 }} />
            Add
          </ToggleButton>
          <ToggleButton
            value="debit"
            aria-label={`Remove ${label}`}
            sx={{ '&.Mui-selected': { bgcolor: `${t.errorFill} !important`, color: t.brandInk } }}
          >
            <Icon name="remove" sx={{ fontSize: 16, mr: 0.5 }} />
            Remove
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField
          id={magnitudeId}
          label="Amount"
          value={edit.magnitude}
          onChange={(e) => onMagnitudeChange(e.target.value)}
          size="small"
          error={overDebit}
          disabled={disabled}
          data-testid={`${bucket.creditBucketId}-amount-field`}
          style={{ width: AMOUNT_WIDTH }}
          slotProps={{
            htmlInput: {
              inputMode: 'decimal',
              'aria-label': `${label} amount`,
              style: { textAlign: 'right' },
            },
          }}
        />
      </Stack>

      {edit.direction === 'grant' && (
        <TextField
          type="date"
          label="Good through (optional)"
          value={edit.expiresAt}
          onChange={(e) => onExpiresAtChange(e.target.value)}
          disabled={disabled}
          error={expiryPast}
          helperText={expiryPast ? 'Pick a future date.' : undefined}
          fullWidth
          size="small"
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { 'aria-label': `${label} expiry` },
          }}
          sx={{ mt: 1.25 }}
        />
      )}

      {delta !== null && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1.25,
            p: '8px 12px',
            borderRadius: 1.5,
            bgcolor: t.raise,
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontSize: 12 }}>
            New balance
          </Typography>
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: 14,
              fontWeight: 500,
              color: overDebit ? t.error : t.text,
              textAlign: 'right',
            }}
          >
            {overDebit ? `${formatCredits(newBalance)} (not allowed)` : formatCredits(newBalance)}
          </Typography>
        </Box>
      )}

      {overDebit && (
        <Alert severity="error" icon={false} role="alert" sx={{ mt: 1, fontSize: 12 }}>
          <strong>Not enough credits.</strong> This bucket only has {formatCredits(bucket.balance)} —
          you can&apos;t remove more than the member has.
        </Alert>
      )}

      {serverError !== null && (
        <Alert severity="error" icon={false} role="alert" sx={{ mt: 1, fontSize: 12 }}>
          {serverError}
        </Alert>
      )}
    </Box>
  );
}
